import React from 'react';
import { Globe, Languages, Clock, Shield } from 'lucide-react';

const GlobalReachPage = () => {
  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Global Reach, Local Impact
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with customers worldwide while maintaining personalized, culturally relevant communication.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <Languages className="h-12 w-12 text-amber-600 mb-6" />
            <h3 className="text-2xl font-bold mb-4">Multi-language Support</h3>
            <p className="text-gray-600">
              Create and manage campaigns in multiple languages with automatic translation suggestions.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <Clock className="h-12 w-12 text-blue-600 mb-6" />
            <h3 className="text-2xl font-bold mb-4">Time Zone Intelligence</h3>
            <p className="text-gray-600">
              Automatically send emails at the optimal time for each recipient's location.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <Globe className="h-12 w-12 text-green-600 mb-6" />
            <h3 className="text-2xl font-bold mb-4">Global Infrastructure</h3>
            <p className="text-gray-600">
              Distributed servers ensure fast delivery and high deliverability worldwide.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <Shield className="h-12 w-12 text-purple-600 mb-6" />
            <h3 className="text-2xl font-bold mb-4">Compliance Tools</h3>
            <p className="text-gray-600">
              Stay compliant with international email and privacy regulations.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 rounded-2xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold mb-8">Global Coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-semibold mb-4">Supported Regions</h4>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  North America
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Europe
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Asia Pacific
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Latin America
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-4">Key Features</h4>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  40+ languages supported
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Regional compliance built-in
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  24/7 global support
                </li>
                <li className="flex items-center">
                  <svg className="h-5 w-5 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Local currency handling
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">190+</div>
              <p className="text-gray-600">Countries served</p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">40+</div>
              <p className="text-gray-600">Languages supported</p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">99.9%</div>
              <p className="text-gray-600">Global uptime</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalReachPage;